import React, { useEffect, useState, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ThemeProvider } from '@mui/material/styles';
import APIWrapper from './utils/graphqlwrapper';
import AppContext from './context';

import ProtectedRoute from './utils/ProtectedRoute';
import logger from './utils/logger';
import { hasAdminRole, hasDataEngineerRole, hasAnalystRole } from './utils/peoplePermissions';

// components
import Menu from './components/Menu';
import Loading from './components/Loading';

// views
import Profile from './views/Profile';
import ClientLandingPage from './views/ClientLanding';
import Homepage from './views/Homepage';
import ClientInfo from './views/ClientInfo';
import Sources from './components/client/Sources';
import Codes from './components/client/Codes';
import TDCAcquisitionsLanding from './views/reporting/TDCAcquisitionsLanding';
import TDCReactivationLanding from './views/reporting/TDCReactivationLanding';
import TDCStrategyLanding from './views/reporting/TDCStrategyLanding';
import SSRMultiChannelLanding from './views/reporting/SSRMultiChannelLanding';
import SSREmailLanding from './views/reporting/SSREmailLanding';
import TransactionsContainer from './views/TransactionsContainer';
import SSRAdsLanding from './views/reporting/SSRAdsLanding';
import EmailSegmentation from './views/reporting/EmailSegmentation';
import RunAdsReport from './components/client/AdsResultsReport';
import TdcProducts from './views/TdcProducts';
import SettingsPage from './views/Settings';
import CallbackPage from './views/Callback';
import NotFound from './views/NotFound';
import Unauthorized from './views/401';
import SBSLanding from './views/reporting/SBSLanding';
import SBSNotificationSnackbar from './components/SBSNotificationSnackbar';
import theme from './components/themes/Theme';
import {
  COOP_ACQ_INVENTORY,
  COOP_ACQ_DELIV_CHECKS,
  COOP_ACQ_DELIV_METRICS,
  COOP_ACQ_DELIV_PERFORMANCE_ANALYSIS,
  COOP_REAC_INVENTORY,
  COOP_REAC_PERFORMANCE,
  COOP_STRAT_CLIENT_REV,
  COOP_STRAT_COOP_DASH,
  COOP_STRAT_MARKETING_TRENDS,
  COOP_STRAT_FIXED_COST_LANDSCAPE,
  SUM_BY_SOURCE_PARAMS,
  SSR_DELIVERABILITY_DASH,
  SSR_CHAMPY_ADS,
} from './utils/reportingConstants';

// auth/protect
import Login from './utils/Login';
import Logout from './utils/Logout';

// styles
import './App.scss';
import AdvancedSearch from './components/AdvancedSearch';
import Insights from './views/Insights';
import ViewReports from './views/reporting/ViewReports';
import HashingPage from './views/HashingPage';

// Maps each Homepage-hosted path to the "view" Homepage should render for it.
const HOMEPAGE_ROUTES = [
  { path: '/', view: 'clientlist' },
  { path: '/dashboard', view: 'clientlist' },
  { path: '/clientlist', view: 'clientlist' },
  { path: '/home', view: 'clientlist' },
  { path: '/client-identities', view: 'clientidentities' },
  { path: '/delivery-settings', view: 'deliverysettings' },
];

function App() {
  const {
    user,
    isAuthenticated,
    getAccessTokenSilently,
    isLoading,
  } = useAuth0();
  const [shouldLogOut, setShouldLogOut] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [auth0Tok, setAuth0Tok] = useState('');
  // useMemo for context will prevent unnecessary rerenders when user and authenticated
  // values have not changed. All future context values should be added here.
  const contextValues = useMemo(
    () => ({
      user,
      authenticated,
      setAuthenticated,
      shouldLogOut,
      setShouldLogOut,
      auth0Tok,
      setAuth0Tok,
    }),
    [user, authenticated, shouldLogOut, auth0Tok],
  );
  const [token, setToken] = useState(null);

  let hasTDCAdminPermission = false;
  let hasDataEngineerPermission = false;
  let hasAnalystPermission = false;

  if (auth0Tok) {
    hasTDCAdminPermission = hasAdminRole(auth0Tok);
    hasDataEngineerPermission = hasDataEngineerRole(auth0Tok);
    hasAnalystPermission = hasAnalystRole(auth0Tok);
  }

  useEffect(() => {
    async function doFedSignIn() {
      // AWS Amplify v6 uses Auth0 as OpenID Connect (OIDC) provider.
      // No need for Amplify.Auth.federatedSignIn() - Auth0 manages authentication.
      // Auth0 token is automatically passed to AppSync API via openidConnect authMode.
      // See graphqlwrapper.js where auth0.getTokenSilently() fetches tokens.
      setUserEmail(user.email);
      logger.debug(
        'Auth0 sign in completed. User is authenticated via Auth0 OIDC.',
        user,
      );
      setAuthenticated(true);
    }

    const getAuth0Token = async () => {
      let accessToken;
      if (isAuthenticated) {
        try {
          accessToken = await getAccessTokenSilently();
          if (!isLoading && accessToken) {
            await doFedSignIn();
          }
          // set auth0Tok used in app context
          setAuth0Tok(accessToken);
        } catch (error) {
          logger.error(error);
          // user is not authenticated or there was an issue with the refresh token, so log them out
          setShouldLogOut(true);
        }
      }
      return accessToken;
    };

    getAuth0Token();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const isMenuOpen = localStorage.getItem('menu-open');
    if (isMenuOpen === null) {
      localStorage.setItem('menu-open', true);
    }
  }, []);

  useEffect(() => {
    async function getToken() {
      const payload = {
        userEmail,
      };

      const input = JSON.stringify(payload);

      let tok;
      try {
        await APIWrapper.queryApi(
          { query: 'getTokenTC' },
          setShouldLogOut,
          { input },
        ).then((resp) => {
          tok = JSON.parse(resp.data[0][0]);
          setToken(tok.body);
        }).catch((e) => logger.error(e));
      } catch (err) {
        logger.error(err);
      }
    }

    if (authenticated) {
      getToken();
    }
  }, [authenticated]);

  if (isLoading) {
    return <Loading fullPage />;
  }
  if (shouldLogOut) {
    return (
      <AppContext.Provider value={contextValues}>
        <Logout />
      </AppContext.Provider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <AppContext.Provider value={contextValues}>
        <Routes>
          {/* The route config is the single source of truth for which Homepage "view" a
              path maps to, rather than Homepage reverse-engineering it from location.pathname.
              key="homepage" stays the same string across all of these so switching between
              them doesn't unmount Homepage (no refetch of client data). */}
          {HOMEPAGE_ROUTES.map(({ path, view }) => (
            <Route
              key="homepage"
              path={path}
              element={(
                <ProtectedRoute
                  component={Menu}
                  body={(
                    <Homepage
                      view={view}
                      hasTDCAdminPermission={hasTDCAdminPermission}
                      hasDataEngineerPermission={hasDataEngineerPermission}
                      hasAnalystPermission={hasAnalystPermission}
                    />
                  )}
                />
              )}
            />
          ))}
          <Route
            path="*"
            element={<ProtectedRoute component={Menu} body={<NotFound />} />}
          />
          <Route
            path="/profile"
            element={<ProtectedRoute component={Menu} body={<Profile />} />}
          />
          <Route
            path="/client/:client/"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={
                  (
                    <ClientLandingPage
                      hasTDCAdminPermission={hasTDCAdminPermission}
                      hasDataEngineerPermission={hasDataEngineerPermission}
                      hasAnalystPermission={hasAnalystPermission}
                    />
                  )
                }
              />
            )}
          />
          <Route
            path="/client/:client/settings"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={
                  (
                    <ClientInfo
                      hasTDCAdminPermission={hasTDCAdminPermission}
                      hasDataEngineerPermission={hasDataEngineerPermission}
                      hasAnalystPermission={hasAnalystPermission}
                    />
                  )
                }
              />
            )}
          />
          <Route
            path="/client/:client/sources"
            exact
            element={
              <ProtectedRoute component={Menu} body={<Sources />} />
            }
          />
          <Route
            path="/client/:client/codes"
            exact
            element={
              <ProtectedRoute component={Menu} body={<Codes />} />
            }
          />
          <Route
            path="/client/:client/transactions"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <TransactionsContainer
                    token={token}
                    authenticated={authenticated}
                    userEmail={userEmail}
                  />
                )}
              />
            )}
          />

          {/* Strategic Services Reports routes */}
          <Route
            path="/client/:client/Strategy/Email"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<SSREmailLanding />}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Email/deliverability_dashboard"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={SSR_DELIVERABILITY_DASH}
                    token={token}
                    heading="Email"
                    subheading="Deliverability Dashboard"
                  />
                )}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Ads"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<SSRAdsLanding />}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Ads/champy_ads_results"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={SSR_CHAMPY_ADS}
                    token={token}
                    heading="Ads"
                    subheading="Champy Ads Results"
                  />
                )}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Ads/run_ads_results"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <RunAdsReport />
                )}
              />
            )}
          />
          {/* Sum by Source */}
          <Route
            path="/client/:client/Strategy/Multi-Channel"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<SSRMultiChannelLanding />}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Multi-Channel/SBS/Download"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<SBSLanding userEmail={userEmail} />}
              />
            )}
          />
          <Route
            path="/client/:client/Strategy/Multi-Channel/SBS/Tableau"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={SUM_BY_SOURCE_PARAMS}
                    token={token}
                    heading="Multi-Channel"
                    subheading="Sum by Source: Tableau Report"
                  />
                )}
              />
            )}
          />

          {/* Benchmarking and Strategy routes */}
          <Route
            path="/client/:client/Co-Op/Strategy"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<TDCStrategyLanding />}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Strategy/client_revenue"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_STRAT_CLIENT_REV}
                    token={token}
                    heading="Strategy"
                    subheading="Client Digital Revenue Trends"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Strategy/digital_coop_dashboard"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_STRAT_COOP_DASH}
                    token={token}
                    heading="Strategy"
                    subheading="Digital Co-Op Dashboard"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Strategy/fixed_cost_landscape"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_STRAT_FIXED_COST_LANDSCAPE}
                    token={token}
                    heading="Strategy"
                    subheading="Fixed Cost Landscape"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Strategy/political_marketing_trends"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_STRAT_MARKETING_TRENDS}
                    token={token}
                    heading="Strategy"
                    subheading="Digital Fundraising Trends"
                  />
                )}
              />
            )}
          />

          {/* Acquisitions routes */}
          <Route
            path="/client/:client/Co-Op/Acquisition"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<TDCAcquisitionsLanding />}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Acquisition/preferred_inventory"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_ACQ_INVENTORY}
                    token={token}
                    heading="Acquisition"
                    subheading="Preferred Inventory"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Acquisition/delivery_qa_dashboard"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_ACQ_DELIV_CHECKS}
                    token={token}
                    heading="Acquisition"
                    subheading="Delivery QA Dashboard"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Acquisition/acquisition_delivery_metrics"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_ACQ_DELIV_METRICS}
                    token={token}
                    heading="Acquisition"
                    subheading="Delivery Metrics"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Acquisition/acquisition_performance_analysis"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_ACQ_DELIV_PERFORMANCE_ANALYSIS}
                    token={token}
                    heading="Acquisition"
                    subheading="Internal Performance Analysis"
                  />
                )}
              />
            )}
          />

          <Route
            path="/client/:client/Co-Op/Acquisition/aggregate_results"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <Insights
                    heading="Acquisition"
                    pageTitle="Acquistions Results"
                    queries={['searchInsightsAcqDeliveries', 'searchInsightsAcqDeliveriesCohorts']}
                  />
                )}
              />
            )}
          />

          {/* Reactivation routes */}
          <Route
            path="/client/:client/Co-Op/Reactivation"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<TDCReactivationLanding />}
              />
            )}
          />
          <Route
            path="/client/:client/Co-Op/Reactivation/reactivation_inventory"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_REAC_INVENTORY}
                    token={token}
                    heading="Reactivation"
                    subheading="Inventory"
                  />
                )}
              />
            )}
          />
          <Route
            path="/client/:client/Co-Op/Reactivation/reactivation_performance"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ViewReports
                    tabInfo={COOP_REAC_PERFORMANCE}
                    token={token}
                    heading="Reactivation"
                    subheading="Performance"
                  />
                )}
              />
            )}
          />
          <Route
            path="/client/:client/Co-Op/Reactivation/aggregate_results"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <Insights
                    heading="Reactivation"
                    pageTitle="Reactivation Results"
                    queries={['searchInsightsReacDeliveries', 'searchInsightsReacDeliveriesCohorts']}
                  />
                )}
              />
            )}
          />

          {/** Delivery Products Routes: Email Segmentation */}
          <Route
            path="/client/:client/Delivery/Email-Segmentation"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={<EmailSegmentation hasTDCAdminPermission={hasTDCAdminPermission} />}
              />
            )}
          />

          {['/client/:client/tdcproducts', '/tdcproducts'].map((path) => (
            <Route
              key="tdcproducts" // doesn't unmount components
              path={path}
              element={(
                <ProtectedRoute
                  component={Menu}
                  body={(
                    <TdcProducts />
                            )}
                />
                        )}
            />
          ))}

          <Route
            path="/settings"
            exact
            element={
              <ProtectedRoute component={Menu} body={<SettingsPage />} />
            }
          />
          <Route
            path="/newclient"
            exact
            element={(
              <ProtectedRoute
                component={Menu}
                body={(
                  <ClientInfo
                    isNew
                    hasTDCAdminPermission={hasTDCAdminPermission}
                    hasDataEngineerPermission={hasDataEngineerPermission}
                    hasAnalystPermission={hasAnalystPermission}
                  />
                )}
              />
            )}
          />
          <Route
            path="/advanced"
            exact
            element={
              <ProtectedRoute component={Menu} body={<AdvancedSearch />} />
            }
          />
          {['/client/:client/hashing', '/hashing'].map((path) => (
            <Route
              key={path}
              path={path}
              element={(
                <ProtectedRoute
                  component={Menu}
                  body={<HashingPage />}
                />
              )}
            />
          ))}
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/login" exact element={<Login />} />
          <Route path="/logout" exact element={<Logout />} />
          <Route path="/401" exact element={<Unauthorized />} />
        </Routes>
        <SBSNotificationSnackbar />
      </AppContext.Provider>
    </ThemeProvider>
  );
}

export default App;
